import { Injectable } from '@angular/core';
import { Observable, of, throwError, Subject } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { AuthApiService } from './auth-api.service';
import { TokenStorageService } from './token-storage.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from '../../environments/environment';
import {Router} from "@angular/router";

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private jwtHelper = new JwtHelperService();
    private refreshInProgress = false;
    private refreshSubject = new Subject<string>();

    constructor(
        private authApi: AuthApiService,
        private tokenStorage: TokenStorageService,
        private router: Router
    ) {}

    /**
     * Autentica al usuario y guarda los tokens retornados por Cognito.
     */
    login(username: string, password: string): Observable<any> {
        return this.authApi.login(username, password).pipe(
            map(response => {
                const result = response.AuthenticationResult;
                if (result?.AccessToken) {
                    this.tokenStorage.setAccessToken(result.AccessToken);
                }
                if (result?.RefreshToken) {
                    this.tokenStorage.setRefreshToken(result.RefreshToken);
                }
                return response;
            }),
            catchError(this.handleError('Error de autenticación. Verifique sus credenciales.'))
        );
    }

    /**
     * Verifica si el usuario está logueado basado en la existencia de un token de acceso válido.
     */
    isLoggedIn(): boolean {
        const token = this.tokenStorage.getAccessToken();
        return !!token && !this.jwtHelper.isTokenExpired(token);
    }

    /**
     * Cierra sesión y elimina todos los tokens almacenados.
     */
    logout(): void {
        this.tokenStorage.clear();
        window.location.href = environment.cognito.logoutUri;
    }

    /**
     * Renueva el token de acceso usando el token de actualización.
     */
    refreshAccessToken(): Observable<string> {
        if (this.refreshInProgress) {
            return this.refreshSubject.asObservable().pipe(take(1));
        }

        const refreshToken = this.tokenStorage.getRefreshToken();
        if (!refreshToken) {
            this.logout();
            return throwError(() => new Error('No se encontró el token de actualización.'));
        }

        this.refreshInProgress = true;

        return this.authApi.refresh(refreshToken).pipe(
            map(response => {
                const newAccessToken = response.AuthenticationResult.AccessToken;
                this.tokenStorage.setAccessToken(newAccessToken);
                this.refreshInProgress = false;
                this.refreshSubject.next(newAccessToken);
                return newAccessToken;
            }),
            catchError(err => {
                this.refreshInProgress = false;
                this.logout();
                return throwError(() => err);
            })
        );
    }

    /**
     * Obtiene el token de acceso almacenado o, si no existe, intenta obtener un token de cliente.
     */
    getAuthToken() {
        const token = this.getToken();
        if (token && !this.jwtHelper.isTokenExpired(token)) {
            return of(token);
        }

        if (this.tokenStorage.getRefreshToken()) {
            return this.refreshAccessToken();
        }

        return throwError(() => {
            this.router.navigate(['/list-search']);
            return new Error('Debe iniciar sesión.');
        });
    }

    /**
     * Obtiene el token almacenado.
     */
    getToken(): string | null {
        return this.tokenStorage.getAccessToken();
    }

    /**
     * Manejador genérico de errores.
     */
    private handleError(message: string) {
        return (error: any): Observable<never> => {
            console.error(message, error);
            return throwError(message);
        };
    }
}
